import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type A11yTheme = 'light' | 'dark' | 'high-contrast';
export type A11yFontScale = 'normal' | 'lg' | 'xl';
export type A11yFontFamily = 'default' | 'dyslexic';

interface A11yState {
  theme: A11yTheme;
  fontScale: A11yFontScale;
  fontFamily: A11yFontFamily;
  setTheme: (t: A11yTheme) => void;
  setFontScale: (s: A11yFontScale) => void;
  setFontFamily: (f: A11yFontFamily) => void;
  reset: () => void;
}

export const useA11yStore = create<A11yState>()(
  persist(
    (set) => ({
      theme: 'light',
      fontScale: 'normal',
      fontFamily: 'default',
      setTheme: (theme) => set({ theme }),
      setFontScale: (fontScale) => set({ fontScale }),
      setFontFamily: (fontFamily) => set({ fontFamily }),
      reset: () => set({ theme: 'light', fontScale: 'normal', fontFamily: 'default' }),
    }),
    { name: 'shopcommand-a11y-prefs' },
  ),
);

/**
 * Applies the current accessibility preferences to the document root.
 * Call once on app startup; the store subscribes to changes itself.
 */
export function initA11yPrefs() {
  const apply = (s: Pick<A11yState, 'theme' | 'fontScale' | 'fontFamily'>) => {
    const root = document.documentElement;
    const theme = s.theme === 'dark' || s.theme === 'high-contrast' ? s.theme : 'light';
    root.setAttribute('data-theme', theme);
    if (s.fontScale === 'normal') root.removeAttribute('data-font-scale');
    else root.setAttribute('data-font-scale', s.fontScale);
    if (s.fontFamily === 'default') root.removeAttribute('data-font-family');
    else root.setAttribute('data-font-family', s.fontFamily);
  };
  apply(useA11yStore.getState());
  useA11yStore.subscribe((s) => apply(s));
}
